import type { PortableTextBlock } from '@portabletext/types';

import { windowEndGameweek } from './gameweek-window';
import { collectArticleReferences, hasArticleBlocks } from './registry';
import { STATS_FIELD_GROUPS } from './stats';
import type {
  ArticleBlockData,
  CollectedReferences,
  StatsRequest
} from './types';
import { fetchLeagueContext } from '@api-service/league-context';
import { fetchPlayerStats } from '@api-service/player-stats';
import type { PlayerStatsRow } from '@api-service/player-stats';
import { fetchPlayers } from '@api-service/players';
import type { Player } from '@api-service/players';
import { fetchTeamFixturesPreview } from '@api-service/team-fixtures-preview';
import type { TeamWithFixturePreview } from '@api-service/team-fixtures-preview';
import { fetchTeams as fetchLeagueTeams } from '@api-service/teams';
import { GAMEWEEK_ONE, MAX_GAMEWEEK } from '@lib/constants';
import {
  fetchClubSquad,
  locateWithinSquad
} from '@lib/page-utils/player-profile/catalogue';
import { toPlayerProfilePath } from '@lib/page-utils/player-profile/slugs';

// `/league/players?optaIds=` accepts at most this many ids per call.
export const OPTA_IDS_PER_REQUEST = 15;
// `/league/players/stats?ids=` accepts at most this many ids per call.
export const STATS_IDS_PER_REQUEST = 100;
// The widest window any player block can ask for; fetching it once covers them all.
export const MAX_WINDOW_GAMEWEEKS = 6;
// Pre-season the API reports no current gameweek; treating it as 0 makes the
// window start at gameweek 1.
const PRE_SEASON_GAMEWEEK = GAMEWEEK_ONE - 1;

export const EMPTY_ARTICLE_BLOCK_DATA: ArticleBlockData = {
  playersByOptaUuid: {},
  profilePathsByOptaUuid: {},
  fixturesByTeamShortName: {},
  teamNamesByShortName: {},
  statsByRequestKey: {},
  currentGameweek: PRE_SEASON_GAMEWEEK
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const fetchPlayersByOptaUuid = async (
  optaUuids: string[]
): Promise<Record<string, Player>> => {
  const pages = await Promise.all(
    chunk(optaUuids, OPTA_IDS_PER_REQUEST).map((ids) =>
      fetchPlayers({
        optaIds: ids.join(','),
        exclude: 'predictions',
        limit: OPTA_IDS_PER_REQUEST
      })
    )
  );
  return Object.fromEntries(
    pages
      .flatMap((page) => page.data)
      .map((player) => [player.externalIds.optaUuid, player])
  );
};

/**
 * Ticker blocks can pin a start gameweek, so the fetched range is the union of
 * the default "next six" window and every pinned range on the page.
 */
export const resolveFixtureRange = (
  currentGameweek: number,
  collected: Pick<CollectedReferences, 'fixtureRanges'>
): { gameweekStart: number; gameweekEnd: number } => {
  let gameweekStart = Math.max(GAMEWEEK_ONE, currentGameweek + 1);
  let gameweekEnd = windowEndGameweek(currentGameweek, MAX_WINDOW_GAMEWEEKS);
  for (const range of collected.fixtureRanges) {
    const start = range.startGameweek ?? gameweekStart;
    gameweekStart = Math.min(gameweekStart, start);
    gameweekEnd = Math.max(
      gameweekEnd,
      Math.min(MAX_GAMEWEEK, start + range.count - 1)
    );
  }
  return { gameweekStart, gameweekEnd };
};

const fetchTeams = async (range: {
  gameweekStart: number;
  gameweekEnd: number;
}): Promise<TeamWithFixturePreview[]> => fetchTeamFixturesPreview(range);

/**
 * Stats are keyed by the API's season player id, so the players call has to
 * land first to translate each optaUuid.
 */
const fetchStatsForRequest = async (
  request: StatsRequest,
  playersByOptaUuid: Record<string, Player>
): Promise<Record<string, PlayerStatsRow>> => {
  const seasonIds = [...request.optaUuids]
    .map((optaUuid) => playersByOptaUuid[optaUuid]?.id)
    .filter((id): id is string => Boolean(id));
  if (seasonIds.length === 0) return {};

  const pages = await Promise.all(
    chunk(seasonIds, STATS_IDS_PER_REQUEST).map((ids) =>
      fetchPlayerStats({
        fields: STATS_FIELD_GROUPS,
        ids: ids.join(','),
        limit: STATS_IDS_PER_REQUEST,
        ...(request.venue !== 'all' && { venue: request.venue }),
        ...(request.minGameweek !== undefined && {
          minGameweek: request.minGameweek
        }),
        ...(request.maxGameweek !== undefined && {
          maxGameweek: request.maxGameweek
        })
      })
    )
  );
  return Object.fromEntries(
    pages.flatMap((page) => page.data).map((row) => [row.optaUuid, row])
  );
};

/**
 * A player's slug is collision-aware (two squad mates sharing a web name get
 * a suffix), so it cannot be built from the player row alone: the whole squad
 * is needed. Squads are fetched once per distinct club rather than once per
 * linked player. A player the API no longer knows gets no path and their
 * mark renders as plain text.
 */
const resolveProfilePaths = async (
  linkedOptaUuids: Set<string>,
  playersByOptaUuid: Record<string, Player>
): Promise<Record<string, string>> => {
  const linkedPlayers = [...linkedOptaUuids]
    .map((optaUuid) => playersByOptaUuid[optaUuid])
    .filter((player): player is Player => Boolean(player));
  if (linkedPlayers.length === 0) return {};

  try {
    const teams = await fetchLeagueTeams();
    const teamsByShortName = new Map(
      teams.map((team) => [team.shortName, team])
    );
    const linkedTeams = [
      ...new Set(
        linkedPlayers
          .map((player) => teamsByShortName.get(player.team.shortName))
          .filter((team): team is (typeof teams)[number] => Boolean(team))
      )
    ];

    const squadsByShortName = new Map(
      await Promise.all(
        linkedTeams.map(
          async (team) =>
            [team.shortName, await fetchClubSquad(team.id)] as const
        )
      )
    );

    const entries: Array<[string, string]> = [];
    for (const player of linkedPlayers) {
      const team = teamsByShortName.get(player.team.shortName);
      const squad = squadsByShortName.get(player.team.shortName);
      const location =
        team && squad
          ? locateWithinSquad(team, squad, player.externalIds.optaUuid)
          : null;
      if (!location) continue;
      entries.push([
        player.externalIds.optaUuid,
        toPlayerProfilePath(location.clubSlug, location.playerSlug)
      ]);
    }
    return Object.fromEntries(entries);
  } catch (error) {
    // Links are an inline extra with a plain-text fallback, so losing them
    // must not take the cards on the same page down with them.
    console.error('[article-blocks] profile path resolution failed', error);
    return {};
  }
};

const fetchCurrentGameweek = async (): Promise<number> =>
  (await fetchLeagueContext())?.currentGameweek?.number ?? PRE_SEASON_GAMEWEEK;

/**
 * Bounded however many blocks the body holds: one context call when a card
 * needs the gameweek, one players call per fifteen ids, one fixtures call, one
 * stats call per distinct venue and gameweek range, and for player links one
 * teams call plus one squad call per linked club. Any failure degrades to
 * empty data so the article still builds; the blocks render their fallback
 * rather than the page 500ing.
 */
export const hydrateArticleBlocks = async (
  body: PortableTextBlock[] | undefined
): Promise<ArticleBlockData> => {
  const collected = collectArticleReferences(body);
  if (!hasArticleBlocks(collected)) return EMPTY_ARTICLE_BLOCK_DATA;

  try {
    // Only fixture-driven cards read the gameweek; a body holding nothing but
    // player links or player lists must not depend on the context endpoint.
    const currentGameweek = collected.needsTeamFixtures
      ? await fetchCurrentGameweek()
      : PRE_SEASON_GAMEWEEK;

    const [playersByOptaUuid, teams] = await Promise.all([
      collected.optaUuids.size > 0
        ? fetchPlayersByOptaUuid([...collected.optaUuids])
        : Promise.resolve({}),
      collected.needsTeamFixtures
        ? fetchTeams(resolveFixtureRange(currentGameweek, collected))
        : Promise.resolve([])
    ]);

    const [statsEntries, profilePathsByOptaUuid] = await Promise.all([
      Promise.all(
        [...collected.statsRequests.values()].map(async (request) => [
          request.key,
          await fetchStatsForRequest(request, playersByOptaUuid)
        ])
      ),
      resolveProfilePaths(collected.linkedOptaUuids, playersByOptaUuid)
    ]);

    return {
      playersByOptaUuid,
      profilePathsByOptaUuid,
      fixturesByTeamShortName: Object.fromEntries(
        teams.map((team) => [team.shortName, team.fixtures])
      ),
      teamNamesByShortName: Object.fromEntries(
        teams.map((team) => [team.shortName, team.name])
      ),
      statsByRequestKey: Object.fromEntries(statsEntries),
      currentGameweek
    };
  } catch (error) {
    console.error('[article-blocks] hydration failed', error);
    return EMPTY_ARTICLE_BLOCK_DATA;
  }
};
